import { renderDocument, type ArticleDoc } from '@archetelos/editor';
import { formatArticleDate } from '@archetelos/lib/date';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';

import { PlayerSurface } from '@/features/watch/PlayerSurface';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { fetchPreviewPlayback } from '@/lib/playbackToken';
import { useAssetMap } from '@/lib/queries/assets';
import { contentQueries } from '@/lib/queries/content';

export const Route = createFileRoute('/creator/content/$contentId/preview')({
  component: PreviewRoute,
});

function PreviewRoute() {
  const { loading } = useRequireRole(['creator'], { creatorStatus: 'approved' });
  const { contentId } = Route.useParams();
  const content = useQuery(contentQueries.byId(contentId));
  const assetMap = useAssetMap((content.data?.body_json as ArticleDoc | null) ?? null);

  if (loading || content.isLoading) {
    return <p className="font-sans text-sm text-ink/50">Loading…</p>;
  }
  const c = content.data;
  if (!c) {
    return <p className="font-sans text-sm text-ink/70">Not found.</p>;
  }

  return (
    <div>
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate/40 bg-slate/10 px-4 py-2">
        <span className="font-sans text-xs uppercase tracking-[0.14em] text-slate">
          Preview — not yet published
        </span>
        <Link
          to="/creator/content/$contentId/edit"
          params={{ contentId }}
          className="font-sans text-xs text-oxblood hover:underline"
        >
          ← Back to editor
        </Link>
      </div>

      <article className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="font-serif text-4xl leading-tight text-ink">{c.title}</h1>
        {c.excerpt && <p className="mt-6 font-serif text-xl italic text-ink/75">{c.excerpt}</p>}
        <p className="mt-6 font-sans text-sm text-ink/60">
          {c.published_at ? formatArticleDate(new Date(c.published_at)) : 'Unpublished draft'}
        </p>
        {c.cover_image_url && (
          <img
            src={c.cover_image_url}
            alt={c.cover_image_alt ?? c.title}
            className="mt-10 w-full rounded-md"
          />
        )}
        {c.type === 'video' && c.video_bunny_id ? (
          <VideoPreview contentId={c.id} title={c.title} />
        ) : (
          <div
            className="prose font-serif drop-cap mt-12"
            dangerouslySetInnerHTML={{
              __html: renderDocument((c.body_json as ArticleDoc | null) ?? null, assetMap),
            }}
          />
        )}
      </article>
    </div>
  );
}

function VideoPreview({ contentId, title }: { contentId: string; title: string }) {
  const playback = useQuery({
    queryKey: ['preview-playback', 'content', contentId] as const,
    queryFn: () => fetchPreviewPlayback({ contentId }),
  });
  return (
    <div className="relative mt-10 aspect-video overflow-hidden rounded-md bg-black">
      {playback.data ? (
        <PlayerSurface playback={playback.data} title={title} />
      ) : (
        <div className="flex h-full items-center justify-center font-sans text-sm text-video-text-faint">
          {playback.isLoading ? 'Loading preview…' : 'Preview unavailable.'}
        </div>
      )}
    </div>
  );
}
